/**
 * Cinematic full-screen sign-in — the boot-gate surface that replaced the
 * strict auth modal (shell.js ensureAuthenticated). Ported from the approved
 * prototype (builder/prototypes/auth-account-demo.html) onto pb-si-* classes.
 *
 * Split layout: left form pane (word-rise Fraunces heading, staggered entries,
 * email/password with lead icons + eye toggle, Google, passkey, "Last used"
 * chip) / right "living canvas" hero (glass mock page, simulated cursor
 * selecting sections in --pb-accent-section violet and elements in
 * --pb-accent-element blue, 3 auto-cycling captioned scenes). Device-theme-
 * aware via self-scoped tokens in styles/auth-screen.css — the light-only
 * builder chrome is untouched.
 *
 * STRICT semantics: resolves ONLY on successful auth; Escape does nothing.
 * Single-flight like showAuthModal. MUST NOT import core/state.js (mounts
 * before store.init).
 *
 * Dev affordances (the local proxy self-passes the gate, so the screen would
 * never render locally without these):
 *   ?auth-screen            — force the screen this load (query param, one-shot)
 *   pb:force-auth-screen=1  — force it every load (localStorage, deliberate)
 * When forced with a passing checkAuth, the payload arrives as devAuthPayload
 * and "Continue as admin (dev only)" resolves it directly.
 */

import { createButton, createModal } from './components.js';
import { eventBus } from '../core/events.js';
import { loginAccount, signupAccount, checkAuth, startGoogleOAuth } from '../templates/auth-api.js';
import { TemplateApiError } from '../templates/template-api.js';
import { escapeHTML } from '../shared/html-escape.js';
import { trapFocus } from './focus-trap.js';
import { GOOGLE_LOGO_SVG } from './auth-modal.js';
import { recordLastUsed, applyLastUsedBadge } from './login-last-used.js';
import {
  getPasskeyAvailability,
  signInWithPasskey,
  listPasskeys,
  isRealUser,
  PasskeyCancelledError
} from '../templates/passkey-api.js';
import { runPasskeyCeremony, offerPasskeyEnrollment } from './passkey-ceremony.js';
import { bindDeviceTheme, bindLoginViewportChrome } from './theme.js';

const PASSWORD_MIN = 8;
const FORCE_KEY = 'pb:force-auth-screen';
const FONT_LINK_ID = 'pb-si-fraunces';

// Post-login passkey enrollment offer: per-account snooze after "Not now",
// and a hard cap on how long the sign-in handoff waits for the eligibility
// check (availability is already session-cached; this bounds listPasskeys).
const ENROLL_SNOOZE_PREFIX = 'pb:pk-enroll-snooze:';
const ENROLL_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const ENROLL_CHECK_TIMEOUT_MS = 2500;

function enrollmentSnoozed(sub) {
  try {
    const at = Number(localStorage.getItem(ENROLL_SNOOZE_PREFIX + sub));
    return Boolean(at) && Date.now() - at < ENROLL_SNOOZE_MS;
  } catch {
    return false;
  }
}

function snoozeEnrollment(sub) {
  try {
    localStorage.setItem(ENROLL_SNOOZE_PREFIX + sub, String(Date.now()));
  } catch {
    /* private mode / disabled storage — non-fatal */
  }
}

/**
 * Should the just-signed-in user be offered a passkey? Only after a
 * password/signup/Google login (a passkey login already has one; dev-admin
 * isn't a real account), on a passkey-capable origin, when the account has no
 * passkeys yet and the offer isn't snoozed. Any failure or slow check resolves
 * false — the offer is never worth delaying the sign-in handoff for.
 */
async function shouldOfferPasskeyEnrollment(result, method) {
  if (method !== 'password' && method !== 'google') return false;
  if (!result?.sub || !isRealUser(result)) return false;
  if (enrollmentSnoozed(result.sub)) return false;
  try {
    const eligibility = (async () => {
      if ((await getPasskeyAvailability()) !== 'available') return false;
      const passkeys = await listPasskeys();
      return passkeys.length === 0;
    })();
    const timeout = new Promise((res) => {
      setTimeout(() => res(false), ENROLL_CHECK_TIMEOUT_MS);
    });
    return await Promise.race([eligibility, timeout]);
  } catch {
    return false; // expected-flow fallback: skip the offer, never block sign-in
  }
}

let _activeScreenPromise = null;

/** True when the dev force flag (query param or localStorage) is set. */
export function isAuthScreenForced() {
  try {
    if (new URLSearchParams(window.location.search).has('auth-screen')) return true;
  } catch {
    /* non-browser */
  }
  try {
    return localStorage.getItem(FORCE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Clear the one-shot query-param force (the localStorage flag is deliberate). */
export function consumeAuthScreenForce() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('auth-screen')) {
      url.searchParams.delete('auth-screen');
      history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
    }
  } catch {
    /* non-fatal */
  }
}

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Lazily load the Fraunces display face (Georgia fallback in the CSS). */
function ensureDisplayFont() {
  if (document.getElementById(FONT_LINK_ID)) return;
  const pre1 = Object.assign(document.createElement('link'), {
    rel: 'preconnect',
    href: 'https://fonts.googleapis.com'
  });
  const pre2 = Object.assign(document.createElement('link'), {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com'
  });
  pre2.crossOrigin = 'anonymous';
  const sheet = Object.assign(document.createElement('link'), {
    id: FONT_LINK_ID,
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400..600&display=swap'
  });
  document.head.append(pre1, pre2, sheet);
}

// ---------------------------------------------------------------------------
// Markup fragments
// ---------------------------------------------------------------------------

const ICONS = {
  mail: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>',
  lock: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>',
  eyeOpen:
    '<svg class="pb-si-eye-open" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>',
  eyeClosed:
    '<svg class="pb-si-eye-closed" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" hidden><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" x2="22" y1="2" y2="22"/></svg>',
  arrow:
    '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>',
  passkey:
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4"/><path d="M14 13.12c0 2.38 0 6.38-1 8.88"/><path d="M17.29 21.02c.12-.6.43-2.3.5-3.02"/><path d="M2 12a10 10 0 0 1 18-6"/><path d="M2 16h.01"/><path d="M21.8 16c.2-2 .131-5.354 0-6"/><path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2"/><path d="M8.65 22c.21-.66.45-1.32.57-2"/><path d="M9 6.8a6 6 0 0 1 9 5.2v2"/></svg>'
};

function titleWordsHtml(text) {
  return text
    .split(' ')
    .map(
      (word, i) =>
        `<span class="pb-si-word-wrap"><span class="pb-si-word" data-pb-si-word-delay="${200 + i * 100}">${escapeHTML(word)}</span></span>`
    )
    .join(' ');
}

/**
 * The entrance stagger's delays are runtime tokens, written through CSSOM (ADR 0004 item 5): a
 * `style="--…"` attribute is dropped under a `style-src 'self'` CSP. The markup states each
 * delay as a data attribute, and this writes it before the element first renders.
 */
function applyEntranceDelays(root) {
  for (const el of root.querySelectorAll('[data-pb-si-delay]')) {
    el.style.setProperty('--pb-si-delay', `${el.dataset.pbSiDelay}ms`);
  }
  for (const el of root.querySelectorAll('[data-pb-si-word-delay]')) {
    el.style.setProperty('--pb-si-word-delay', `${el.dataset.pbSiWordDelay}ms`);
  }
}

function heroHtml() {
  return `
  <section class="pb-si-hero" aria-label="PageStitch feature preview">
    <div class="pb-si-hero-frame">
      <div class="pb-si-aurora"></div>
      <div class="pb-si-grid"></div>
      <div class="pb-si-page" data-hero-page>
        <div class="pb-si-mock-section pb-si-mock-nav" data-label="Navigation" data-sec="nav">
          <span class="pb-si-mock-dot"></span>
          <span class="pb-si-mock-line"></span><span class="pb-si-mock-line"></span><span class="pb-si-mock-line"></span>
          <span class="pb-si-mock-line"></span>
        </div>
        <div class="pb-si-mock-section pb-si-mock-hero" data-label="Hero" data-sec="hero">
          <div class="pb-si-mock-line pb-si-title-line" data-el="headline"></div>
          <div class="pb-si-mock-line pb-si-sub-line"></div>
          <div class="pb-si-mock-cta-row"><span class="pb-si-mock-cta" data-el="button"></span><span class="pb-si-mock-cta pb-si-ghost"></span></div>
        </div>
        <div class="pb-si-mock-section" data-label="Services" data-sec="cards">
          <div class="pb-si-mock-cards">
            <div class="pb-si-mock-card" data-el="card"><div class="pb-si-mock-thumb"></div><div class="pb-si-mock-line"></div><div class="pb-si-mock-line pb-si-short"></div></div>
            <div class="pb-si-mock-card"><div class="pb-si-mock-thumb"></div><div class="pb-si-mock-line"></div><div class="pb-si-mock-line pb-si-short"></div></div>
            <div class="pb-si-mock-card"><div class="pb-si-mock-thumb"></div><div class="pb-si-mock-line"></div><div class="pb-si-mock-line pb-si-short"></div></div>
          </div>
        </div>
        <div class="pb-si-mock-section pb-si-mock-footer" data-label="Footer" data-sec="footer">
          <span class="pb-si-mock-line"></span><span class="pb-si-mock-line"></span><span class="pb-si-mock-line"></span>
        </div>
      </div>
      <div class="pb-si-cursor" data-hero-cursor data-tag="">
        <svg viewBox="0 0 24 24" fill="#fff" stroke="#0a0b0d" stroke-width="1.4" aria-hidden="true"><path d="M5 3l14 8-6.5 1.5L9 19 5 3z"/></svg>
      </div>
      <div class="pb-si-scrim"></div>
      <div class="pb-si-caption">
        <div class="pb-si-caption-copy">
          <h3 data-hero-title></h3>
          <p data-hero-sub></p>
        </div>
        <div class="pb-si-dots" data-hero-dots role="group" aria-label="Feature scenes">
          <button type="button" class="pb-si-dot" aria-label="Build pages, not boilerplate" aria-pressed="false"></button>
          <button type="button" class="pb-si-dot" aria-label="Every block is editable" aria-pressed="false"></button>
          <button type="button" class="pb-si-dot" aria-label="Export a real site" aria-pressed="false"></button>
        </div>
      </div>
    </div>
  </section>`;
}

// ---------------------------------------------------------------------------
// Hero scene choreography
// ---------------------------------------------------------------------------

const SCENES = [
  {
    title: 'Build pages, not boilerplate.',
    sub: 'Sections snap together on a live canvas — violet marks the section, blue the element under your cursor.',
    steps: [
      { sec: 'hero', kind: 'section', tag: 'Hero · section' },
      { sec: 'hero', el: 'headline', kind: 'element', tag: 'Headline · h1' },
      { sec: 'hero', el: 'button', kind: 'element', tag: 'Button · CTA' }
    ]
  },
  {
    title: 'Every block is editable.',
    sub: 'Click into any card, swap the copy, and the theme keeps everything on-brand.',
    steps: [
      { sec: 'cards', kind: 'section', tag: 'Services · section' },
      { sec: 'cards', el: 'card', kind: 'element', tag: 'Card · element' },
      { sec: 'nav', kind: 'section', tag: 'Navigation · shared' }
    ]
  },
  {
    title: 'Export a real site.',
    sub: 'One click hands you a clean Eleventy project — no lock-in, just fast pages.',
    steps: [
      { sec: 'footer', kind: 'section', tag: 'Footer · shared' },
      { sec: 'hero', kind: 'section', tag: 'Hero · section' },
      { sec: 'hero', el: 'headline', kind: 'element', tag: 'Headline · h1' }
    ]
  }
];

class HeroScenes {
  constructor(shellEl) {
    this.shell = shellEl;
    this.frame = shellEl.querySelector('.pb-si-hero-frame');
    this.page = shellEl.querySelector('[data-hero-page]');
    this.cursor = shellEl.querySelector('[data-hero-cursor]');
    this.titleEl = shellEl.querySelector('[data-hero-title]');
    this.subEl = shellEl.querySelector('[data-hero-sub]');
    this.dots = [...shellEl.querySelectorAll('[data-hero-dots] .pb-si-dot')];
    this.sceneIdx = 0;
    this.stepIdx = 0;
    this.timer = null;
    this.reduced = prefersReducedMotion();
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.setScene(i)));
  }

  start() {
    if (!this.frame) return;
    this.cursor.style.transform = 'translate(60%, 40%)';
    this.setScene(0);
  }

  destroy() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  clearSelections() {
    this.page
      ?.querySelectorAll('.pb-si-sel-section')
      .forEach((n) => n.classList.remove('pb-si-sel-section'));
    this.page?.querySelectorAll('.pb-si-el-sel').forEach((n) => n.classList.remove('pb-si-el-sel'));
  }

  moveCursorTo(el) {
    if (!this.frame?.isConnected || !el) return;
    const fr = this.frame.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const x = r.left - fr.left + r.width * 0.62;
    const y = r.top - fr.top + r.height * 0.58;
    this.cursor.style.transform = `translate(${x}px, ${y}px)`;
  }

  setScene(idx) {
    clearTimeout(this.timer);
    this.clearSelections();
    this.cursor.classList.remove('pb-si-tagged');
    this.sceneIdx = idx;
    this.stepIdx = 0;
    this.dots.forEach((dot, i) => {
      const active = i === idx;
      dot.classList.toggle('pb-si-dot-active', active);
      dot.setAttribute('aria-pressed', String(active));
    });
    this.titleEl.textContent = SCENES[idx].title;
    this.subEl.textContent = SCENES[idx].sub;
    // Reduced motion: captions + dots only — no cursor choreography.
    if (!this.reduced) {
      this.timer = setTimeout(() => this.playStep(), 600);
    }
  }

  playStep() {
    if (!this.shell.isConnected) return;
    const scene = SCENES[this.sceneIdx];
    const step = scene.steps[this.stepIdx];
    const secEl = this.page?.querySelector(`[data-sec="${step.sec}"]`);
    if (!secEl) return;
    const target = step.el ? secEl.querySelector(`[data-el="${step.el}"]`) : secEl;
    this.moveCursorTo(target || secEl);

    this.timer = setTimeout(() => {
      if (!this.shell.isConnected) return;
      this.clearSelections();
      this.cursor.setAttribute('data-tag', step.tag);
      this.cursor.classList.add('pb-si-tagged');
      this.cursor.classList.toggle('pb-si-tag-section', step.kind === 'section');
      secEl.classList.add('pb-si-sel-section');
      if (step.kind === 'element' && target) target.classList.add('pb-si-el-sel');

      this.timer = setTimeout(() => {
        this.cursor.classList.remove('pb-si-tagged');
        this.stepIdx += 1;
        if (this.stepIdx >= scene.steps.length) {
          this.setScene((this.sceneIdx + 1) % SCENES.length);
        } else {
          this.playStep();
        }
      }, 2100);
    }, 1200);
  }
}

/** An operator route exists only when boot configuration supplies an email. */
function supportEmail() {
  const value = globalThis.window?.__PB_SUPPORT_EMAIL;
  if (typeof value !== 'string') return '';
  const email = value.trim();
  return /^[^\s@<>"'?#]+@[^\s@<>"'?#]+\.[^\s@<>"'?#]+$/.test(email) ? email : '';
}

function showSignInHelp({ googleButton, passkeyButton }) {
  const modal = createModal({
    title: "Can't sign in?",
    testid: 'signin-help',
    guardSelector: '[data-testid="signin-help"]',
    maxWidth: 480
  });
  if (!modal) return;
  const paragraph = (text) => {
    const p = document.createElement('p');
    p.textContent = text;
    modal.body.appendChild(p);
  };
  paragraph(
    'Continue with Google using the same email address as your PageStitch account. Choosing a different email opens a different account.'
  );
  modal.body.appendChild(
    createButton({
      label: 'Continue with Google',
      variant: 'outline',
      disabled: googleButton.disabled,
      onClick: () => {
        modal.close();
        googleButton.click();
      }
    })
  );
  paragraph(
    'If you created a passkey for this account, use it on a device or password manager that has that passkey. You can sign in without entering your PageStitch password.'
  );
  if (passkeyButton) {
    modal.body.appendChild(
      createButton({
        label: 'Sign in with a passkey',
        variant: 'outline',
        disabled: passkeyButton.disabled,
        onClick: () => {
          modal.close();
          passkeyButton.click();
        }
      })
    );
  } else {
    paragraph(
      'The passkey option appears on the sign-in screen when it is available for this browser. Try the browser or device where you saved your passkey.'
    );
  }
  const email = supportEmail();
  if (email) {
    const p = document.createElement('p');
    const link = document.createElement('a');
    link.href = `mailto:${encodeURIComponent(email)}`;
    link.textContent = `Contact the operator: ${email}`;
    p.appendChild(link);
    modal.body.appendChild(p);
  }
  return modal;
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

/**
 * Show the sign-in screen. Single-flight; resolves ONLY on successful auth.
 * @param {{email?: string, devAuthPayload?: object|null}} [options]
 * @returns {Promise<object>} the auth payload
 */
export function showAuthScreen(options = {}) {
  if (_activeScreenPromise) return _activeScreenPromise;
  _activeScreenPromise = _openAuthScreen(options);
  _activeScreenPromise.finally(() => {
    _activeScreenPromise = null;
  });
  return _activeScreenPromise;
}

function _openAuthScreen(options) {
  return new Promise((resolve) => {
    ensureDisplayFont();
    const devPayload = options.devAuthPayload || null;
    const adminVisible = Boolean(globalThis.window?.__PB_ALLOW_LEGACY_ADMIN) || Boolean(devPayload);
    let mode = 'login';
    let closed = false;

    const shellEl = document.createElement('div');
    shellEl.className = 'pb-si-shell';
    shellEl.setAttribute('role', 'main');
    // Bind the colour register BEFORE the element is in the document, so the
    // first paint is already in the right palette. This replaces the
    // `prefers-color-scheme: light` block auth-screen.css used to carry: the
    // values now come from the shared `.pb-dark` register in tokens-builder.css.
    const releaseTheme = bindDeviceTheme(shellEl);
    let releaseViewportChrome = () => {};
    shellEl.innerHTML = `
      <span class="pb-si-brand">
        <img src="assets/brand/icon-256.png" alt="PageStitch" width="48" height="48">
      </span>
      <section class="pb-si-form-pane">
        <div class="pb-si-form-inner">
          <h1 class="pb-si-title" id="pb-si-title" aria-label="Welcome back.">${titleWordsHtml('Welcome back.')}</h1>
          <p class="pb-si-lede pb-si-enter" data-pb-si-delay="420" id="pb-si-lede">Sign in to your PageStitch workspace and pick up where your pages left off.</p>
          <form id="pb-si-form" autocomplete="on">
            <div class="pb-si-field pb-si-enter" data-pb-si-delay="500">
              <label class="pb-si-label" for="pb-si-email">Email address</label>
              <div class="pb-si-input-wrap">
                <span class="pb-si-lead-icon">${ICONS.mail}</span>
                <input type="email" id="pb-si-email" class="pb-si-input" autocomplete="email" placeholder="you@example.com" required value="${escapeHTML(options.email || '')}" />
              </div>
            </div>
            <div class="pb-si-field pb-si-enter" data-pb-si-delay="580">
              <label class="pb-si-label" for="pb-si-password">Password</label>
              <div class="pb-si-input-wrap">
                <span class="pb-si-lead-icon">${ICONS.lock}</span>
                <input type="password" id="pb-si-password" class="pb-si-input pb-si-input-eye" autocomplete="current-password" placeholder="${PASSWORD_MIN}+ characters" minlength="${PASSWORD_MIN}" required />
                <button type="button" class="pb-si-eye" id="pb-si-eye" aria-label="Show password" aria-pressed="false">${ICONS.eyeOpen}${ICONS.eyeClosed}</button>
              </div>
            </div>
            <div class="pb-si-row pb-si-enter" data-pb-si-delay="660">
              <button type="button" class="pb-si-link" id="pb-si-mode-toggle">New here? Create an account</button>
            </div>
            <div class="pb-si-method pb-si-enter" data-pb-si-delay="740" data-login-method="password">
              <button type="submit" class="pb-si-btn-primary" id="pb-si-submit">
                <span>Sign In</span>
                ${ICONS.arrow}
              </button>
            </div>
          </form>
          <div class="pb-si-divider pb-si-enter" data-pb-si-delay="820"><span>or continue with</span></div>
          <div class="pb-si-method pb-si-enter" data-pb-si-delay="880" data-login-method="google">
            <button type="button" class="pb-si-btn-secondary" id="pb-si-google">
              ${GOOGLE_LOGO_SVG}
              Continue with Google
            </button>
          </div>
          <div id="pb-si-passkey-host"></div>
          <div id="pb-si-error" class="pb-si-alert pb-si-alert-error" role="alert" hidden></div>
          <div id="pb-si-info" class="pb-si-alert pb-si-alert-info" aria-live="polite" hidden></div>
          ${
            adminVisible
              ? `
          <p class="pb-si-admin-row"><button type="button" class="pb-si-link" id="pb-si-admin">Continue as admin (dev only)</button></p>
          `
              : ''
          }
          <p class="pb-si-copyright pb-si-enter" data-pb-si-delay="960">© ${new Date().getFullYear()} PageStitch. All rights reserved.</p>
        </div>
      </section>
      ${heroHtml()}`;
    applyEntranceDelays(shellEl);

    document.body.appendChild(shellEl);
    releaseViewportChrome = bindLoginViewportChrome(shellEl);
    const mountTime = performance.now(); // anchor for the async passkey entrance
    const releaseTrap = trapFocus(shellEl); // strict: 'trap-escape' is ignored

    const form = shellEl.querySelector('#pb-si-form');
    const titleEl = shellEl.querySelector('#pb-si-title');
    const ledeEl = shellEl.querySelector('#pb-si-lede');
    const emailInput = shellEl.querySelector('#pb-si-email');
    const passwordInput = shellEl.querySelector('#pb-si-password');
    const submitBtn = shellEl.querySelector('#pb-si-submit');
    const googleBtn = shellEl.querySelector('#pb-si-google');
    const modeToggle = shellEl.querySelector('#pb-si-mode-toggle');
    const errorEl = shellEl.querySelector('#pb-si-error');
    const infoEl = shellEl.querySelector('#pb-si-info');
    const passkeyHost = shellEl.querySelector('#pb-si-passkey-host');
    let helpModal = null;
    const helpButton = createButton({
      variant: 'bare',
      className: 'pb-si-link',
      label: "Can't sign in?",
      testid: 'signin-help-trigger',
      onClick: () => {
        helpModal = showSignInHelp({
          googleButton: googleBtn,
          passkeyButton: shellEl.querySelector('#pb-si-passkey')
        });
      }
    });
    passkeyHost.after(helpButton);

    const hero = new HeroScenes(shellEl);
    hero.start();

    function showError(text) {
      infoEl.hidden = true;
      errorEl.textContent = text;
      errorEl.hidden = false;
    }
    function showInfo(text) {
      errorEl.hidden = true;
      infoEl.textContent = text;
      infoEl.hidden = false;
    }
    function hideAlerts() {
      errorEl.hidden = true;
      infoEl.hidden = true;
    }

    function setBusy(busy) {
      submitBtn.disabled = busy;
      emailInput.disabled = busy;
      passwordInput.disabled = busy;
      googleBtn.disabled = busy;
      helpButton.disabled = busy;
      const passkeyBtn = shellEl.querySelector('#pb-si-passkey');
      if (passkeyBtn) passkeyBtn.disabled = busy;
    }

    function setMode(next) {
      mode = next === 'signup' ? 'signup' : 'login';
      const isSignup = mode === 'signup';
      titleEl.innerHTML = titleWordsHtml(isSignup ? 'Create your account.' : 'Welcome back.');
      applyEntranceDelays(titleEl);
      titleEl.setAttribute('aria-label', isSignup ? 'Create your account.' : 'Welcome back.');
      ledeEl.textContent = isSignup
        ? 'PageStitch is in private beta — you’ll need an invited email address. An account keeps your projects synced and recoverable.'
        : 'Sign in to your PageStitch workspace and pick up where your pages left off.';
      submitBtn.querySelector('span').textContent = isSignup ? 'Create Account' : 'Sign In';
      passwordInput.autocomplete = isSignup ? 'new-password' : 'current-password';
      modeToggle.textContent = isSignup
        ? 'Already have an account? Sign in'
        : 'New here? Create an account';
      hideAlerts();
    }
    modeToggle.addEventListener('click', () => setMode(mode === 'signup' ? 'login' : 'signup'));

    // Eye toggle (aria-pressed pattern from the reference login).
    const eyeBtn = shellEl.querySelector('#pb-si-eye');
    eyeBtn.addEventListener('click', () => {
      const showing = passwordInput.type === 'text';
      passwordInput.type = showing ? 'password' : 'text';
      eyeBtn.setAttribute('aria-pressed', showing ? 'false' : 'true');
      eyeBtn.setAttribute('aria-label', showing ? 'Show password' : 'Hide password');
      eyeBtn.querySelector('.pb-si-eye-open').hidden = !showing;
      eyeBtn.querySelector('.pb-si-eye-closed').hidden = showing;
    });

    async function succeed(result, method) {
      if (closed) return;
      closed = true;
      if (method) recordLastUsed(method); // dev-admin passes null: not a method
      eventBus.emit('auth:changed', result);
      // The "enable Face ID" moment: offer passkey enrollment over the still-
      // running hero before the screen leaves. Dismissal snoozes per account;
      // any offer failure falls through to the normal handoff.
      try {
        if (await shouldOfferPasskeyEnrollment(result, method)) {
          const outcome = await offerPasskeyEnrollment();
          if (outcome === 'dismissed') snoozeEnrollment(result.sub);
        }
      } catch {
        /* never block sign-in on the offer */
      }
      helpModal?.close();
      hero.destroy();
      const finish = () => {
        releaseTrap();
        releaseViewportChrome();
        releaseTheme();
        shellEl.remove();
        resolve(result);
      };
      if (prefersReducedMotion()) {
        finish();
      } else {
        shellEl.classList.add('pb-si-leave');
        shellEl.addEventListener('animationend', finish, { once: true });
        setTimeout(finish, 600); // safety net if animationend never fires
      }
    }

    // --- Password / signup ---
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const email = emailInput.value.trim().toLowerCase();
      const password = passwordInput.value;
      if (!email || !password) {
        showError('Email and password are required.');
        return;
      }
      if (password.length < PASSWORD_MIN) {
        showError(`Password must be at least ${PASSWORD_MIN} characters.`);
        return;
      }
      hideAlerts();
      setBusy(true);
      const label = submitBtn.querySelector('span');
      const original = label.textContent;
      label.textContent = mode === 'signup' ? 'Creating account…' : 'Signing in…';
      try {
        const result =
          mode === 'signup'
            ? await signupAccount({ email, password })
            : await loginAccount({ email, password });
        void succeed(result, 'password');
      } catch (err) {
        const detail = err instanceof TemplateApiError ? err.message : 'Auth request failed';
        showError(detail);
        label.textContent = original;
        setBusy(false);
      }
    });

    // --- Google (window.open MUST be synchronous in the click handler) ---
    googleBtn.addEventListener('click', async () => {
      hideAlerts();
      let result;
      try {
        const oauthPromise = startGoogleOAuth(); // sync popup open
        setBusy(true);
        showInfo('Waiting for Google…');
        result = await oauthPromise;
      } catch (err) {
        const detail = err instanceof TemplateApiError ? err.message : 'Google sign-in failed';
        if (detail === 'Cancelled') hideAlerts();
        else showError(detail);
        setBusy(false);
        return;
      }
      void succeed(result, 'google');
    });

    // --- Passkey (rendered only when the backend supports this origin) ---
    getPasskeyAvailability().then((availability) => {
      if (closed || availability !== 'available') {
        applyLastUsedBadge(shellEl);
        return;
      }
      const wrap = document.createElement('div');
      wrap.className = 'pb-si-method pb-si-method-passkey pb-si-enter';
      // Continue the entrance choreography: the passkey button sits just after
      // Google (880ms). Anchor the delay to the screen's mount time so it slots
      // into the stagger if availability resolves in time, or plays immediately
      // (still animated, via `both`) if it resolves after the sequence.
      const passkeyDelay = Math.max(0, 940 - (performance.now() - mountTime));
      wrap.style.setProperty('--pb-si-delay', `${passkeyDelay}ms`);
      wrap.dataset.loginMethod = 'passkey';
      wrap.innerHTML = `
        <button type="button" class="pb-si-btn-secondary" id="pb-si-passkey">
          ${ICONS.passkey}
          Sign in with a passkey
        </button>`;
      passkeyHost.appendChild(wrap);
      applyLastUsedBadge(shellEl);

      wrap.querySelector('#pb-si-passkey').addEventListener('click', async () => {
        hideAlerts();
        setBusy(true);
        try {
          const result = await runPasskeyCeremony((signal) => signInWithPasskey({ signal }), {
            title: 'Use your passkey',
            sub: 'Confirm with Touch ID, Face ID, or your device PIN to continue.'
          });
          void succeed(result, 'passkey');
          return;
        } catch (err) {
          if (!(err instanceof PasskeyCancelledError)) {
            showError(err instanceof TemplateApiError ? err.message : 'Passkey sign-in failed.');
          }
          // Cancel = documented expected flow: restore the form silently.
        }
        setBusy(false);
      });
    });

    // --- Dev admin ---
    const adminBtn = shellEl.querySelector('#pb-si-admin');
    if (adminBtn) {
      adminBtn.addEventListener('click', async () => {
        hideAlerts();
        if (devPayload) {
          // Forced-screen preview over a passing local gate: resolve directly.
          void succeed(devPayload, null);
          return;
        }
        setBusy(true);
        showInfo('Continuing as admin…');
        try {
          const result = await checkAuth();
          void succeed(result, null);
        } catch (err) {
          showError(err instanceof TemplateApiError ? err.message : 'Auth check failed');
          setBusy(false);
        }
      });
    }

    // A notice the CALLER already knows about, shown before the user does
    // anything. Today's only source is the private-beta gate: boot found a
    // valid session whose account is no longer on the access list, and the
    // sign-in screen must say so rather than present a blank form that will
    // refuse the same credentials the user is about to re-type.
    if (options.initialNotice) showError(String(options.initialNotice));

    setTimeout(() => emailInput.focus(), 0);
  });
}
